import { API_URL, apiRequest, safeJsonParse } from "../config/api";

export interface AssetManufacturer {
  name: string;
  description: string;
  website: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  country: string | null;
  id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface AssetWarranty {
  project_material_id: string;
  unit_id: string | null;
  manufacturer_id: string;
  warranty_start_date: string;
  warranty_end_date: string;
  warranty_provider: string;
  warranty_terms: string | null;
  coverage_details: string | null;
  serial_number: string;
  model: string;
  id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
  is_active_warranty: boolean;
  days_remaining: number;
  warranty_status: string;
  manufacturer: AssetManufacturer;
}

export interface AssetResponsiblePerson {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
    is_active: boolean;
  is_email_verified: boolean;
    created_at: string;
    updated_at: string | null;
  active_to: string | null;
  id: string;
}

export interface AssetStatus {
  project_id: string;
  name: string;
  description: string | null;
  status_order: number;
  requires_responsible_person: boolean;
  requires_installation: boolean;
  requires_documentation: boolean;
  estimated_duration_days: number | null;
  max_duration_days: number | null;
  id: string;
  created_at: string;
  updated_at: string | null;
  version: number;
}

export interface AssetUnit {
  id: string;
  project_id: string;
  site_id: string;
  floor_id: string;
  room_id: string;
  row_id: string;
  rack_id: string;
  position: number;
  reserved_units: number;
  type: string | null;
  device_model: string;
  power_usage: number;
  horizontal_position: string;
}

export interface ProjectAsset {
  id: string;
  project_id: string;
  item: string;
  name: string;
  description: string;
  quantity: number;
  ordered_at: string;
  eta: string;
  tracking_number: string | null;
  lead_time: string | null;
  esd: string | null;
  isd: string | null;
  unit_id: string | null;
  current_status_id: string;
  current_responsible_person_id: string | null;
  is_installed: boolean;
  notes: string | null;
  warranty: AssetWarranty | null;
  status: AssetStatus;
  unit: AssetUnit | null;
  current_responsible_person?: AssetResponsiblePerson | null; // Available in lifecycle API
  created_at: string;
  updated_at: string | null;
  version: number;
  // New fields added by backend for direct rack navigation
  site?: string;
  floor?: string;
  room?: string;
  row?: string;
  rack?: string;
}

// Lifecycle entry is essentially a full ProjectAsset with all relationships populated
export interface AssetLifecycleEntry extends ProjectAsset {
  current_responsible_person: AssetResponsiblePerson | null;
}

// Response type for lifecycle API
export type AssetLifecycle = AssetLifecycleEntry[];

export interface AssetsResponse {
  total: number;
  page: number;
  size: number;
  results: ProjectAsset[];
}

export interface CreateAssetRequest {
  project_id: string;
  item: string;
  name: string;
  description: string;
  quantity: number;
  current_status_id: string; // Required field per backend
  current_responsible_person_id?: string | null; // Auto-assign creator
  is_installed: boolean; // Required field per backend
  ordered_at?: string;
  eta?: string;
  tracking_number?: string | null;
  lead_time?: string | null;
  esd?: string | null;
  isd?: string | null;
}

export interface UpdateAssetRequest {
  project_id: string;
  item?: string;
  name?: string;
  description?: string;
  quantity?: number;
  ordered_at?: string;
  eta?: string;
  tracking_number?: string | null;
  lead_time?: string | null;
  esd?: string | null;
  isd?: string | null;
  unit_id?: string | null;
  current_status_id?: string | null;
  current_responsible_person_id?: string | null;
  is_installed?: boolean;
  notes?: string | null;
}

export interface CreateStatusRequest {
  project_id: string;
  name: string;
  description?: string;
  status_order: number;
  requires_responsible_person: boolean;
  requires_installation: boolean;
  requires_documentation: boolean;
  estimated_duration_days: number; // Required by API
  max_duration_days: number; // Required by API
}

export interface UpdateStatusRequest {
  project_id: string;
  name: string;
  description?: string;
  status_order: number;
  requires_responsible_person: boolean;
  requires_installation: boolean;
  requires_documentation: boolean;
  estimated_duration_days: number; // Required by API
  max_duration_days: number; // Required by API
  id: string;
}

export interface StatusesResponse {
  total: number;
  page: number;
  size: number;
  results: AssetStatus[];
}

export interface UpdateWarrantyRequest {
  project_material_id: string;
  unit_id?: string | null;
  manufacturer_id: string;
  warranty_start_date: string;
  warranty_end_date: string;
  warranty_provider: string;
  warranty_terms?: string | null;
  coverage_details?: string | null;
  serial_number: string;
  model: string;
  is_active?: boolean;
}

/**
 * Get all assets for a project with automatic token refresh on 401 errors
 */
export const getProjectAssets = async (
  token: string, 
  projectId: string,
  keycloak?: any
): Promise<AssetsResponse> => {
  try {
    const response = await apiRequest(`${API_URL}/projects/${projectId}/assets`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    }, keycloak);

    const data: AssetsResponse = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching project assets:', error);
    throw new Error(`Failed to fetch project assets: ${error.message}`);
  }
};

/**
 * Create a new asset for a project
 */
export const createProjectAsset = async (token: string, projectId: string, assetData: CreateAssetRequest): Promise<ProjectAsset> => {
  try {
    console.log('🚀 Creating asset with data:', JSON.stringify(assetData, null, 2));
    
    const response = await fetch(`${API_URL}/projects/${projectId}/assets`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(assetData)
    });

    console.log('📡 API Response status:', response.status, response.statusText);

    if (!response.ok) {
      // Try to get detailed error from response body
      let errorDetails = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorBody = await response.text();
        console.error('❌ Full error response:', errorBody);
        
        // Try to parse as JSON for structured errors
        try {
          const errorJson = JSON.parse(errorBody);
          console.error('📋 Parsed error JSON:', errorJson);
          
          if (errorJson.detail) {
            errorDetails = errorJson.detail;
          } else if (errorJson.message) {
            errorDetails = errorJson.message;
          } else if (Array.isArray(errorJson)) {
            // Handle validation errors array
            errorDetails = errorJson.map(e => e.message || e.msg || JSON.stringify(e)).join(', ');
          }
        } catch (parseError) {
          // If not JSON, use raw text
          if (errorBody && errorBody !== response.statusText) {
            errorDetails += ` - ${errorBody}`;
          }
        }
      } catch (bodyError) {
        console.error('❌ Could not read error response body:', bodyError);
      }
      
      throw new Error(errorDetails);
    }

    const data: ProjectAsset = await response.json();
    console.log('✅ Asset created successfully:', data);
    return data;
  } catch (error: any) {
    console.error('❌ Error creating project asset:', error);
    throw error; // Re-throw the original error with details
  }
};

/**
 * Get a specific asset by ID
 */
export const getProjectAsset = async (token: string, projectId: string, assetId: string): Promise<ProjectAsset> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/${assetId}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: ProjectAsset = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching project asset:', error);
    throw new Error(`Failed to fetch project asset: ${error.message}`);
  }
};

/**
 * Get asset by unit_id (searches through all project assets)
 */
export const getProjectAssetByUnitId = async (
  token: string, 
  projectId: string, 
  unitId: string,
  keycloak?: any
): Promise<ProjectAsset | null> => {
  try {
    console.log('🔍 Searching for asset with unit_id:', unitId, 'in project:', projectId);
    
    // Get all assets for the project
    const response = await getProjectAssets(token, projectId, keycloak);
    
    // Find asset with matching unit_id
    const asset = response.results.find(a => a.unit_id === unitId);
    
    if (asset) {
      console.log('✅ Found asset:', asset.name, 'with unit_id:', unitId);
      return asset;
    } else {
      console.log('❌ No asset found with unit_id:', unitId);
      return null;
    }
  } catch (error: any) {
    console.error('Error fetching project asset by unit_id:', error);
    throw new Error(`Failed to fetch project asset by unit_id: ${error.message}`);
  }
};


/**
 * Update an existing asset with automatic token refresh
 */
export const updateProjectAsset = async (
  token: string, 
  projectId: string, 
  assetId: string, 
  assetData: UpdateAssetRequest,
  keycloak?: any
): Promise<ProjectAsset> => {
  try {
    console.log('🔧 updateProjectAsset called with:', {
      projectId,
      assetId,
      current_status_id: assetData.current_status_id,
      is_installed: assetData.is_installed,
      unit_id: assetData.unit_id,
      assetData: assetData
    });

    const response = await apiRequest(`${API_URL}/projects/${projectId}/assets/${assetId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(assetData)
    }, keycloak);

    console.log('📡 Update API Response status:', response.status, response.statusText);

    const data = await safeJsonParse(response);
    
    console.log('📥 Raw API response data:', data);
    
    // If server returns empty response, return the updated asset data
    if (!data) {
      console.log('⚠️ Server returned empty response, using local data');
      return {
        id: assetId,
        ...assetData
      } as ProjectAsset;
    }
    
    console.log('✅ Asset updated successfully. Final status_id:', data.current_status_id, 'is_installed:', data.is_installed);
    return data;
  } catch (error: any) {
    if (error.message?.includes('JSON')) {
      console.warn('⚠️ JSON parse error - asset may have been updated successfully');
      throw new Error('Server returned invalid response. The asset may have been updated successfully.');
    }
    console.error('❌ Error updating project asset:', error);
    throw new Error(`Failed to update project asset: ${error.message}`);
  }
};

/**
 * Delete an asset
 */
export const deleteProjectAsset = async (token: string, projectId: string, assetId: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/${assetId}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error: any) {
    console.error('Error deleting project asset:', error);
    throw new Error(`Failed to delete project asset: ${error.message}`);
  }
};

/**
 * Get all asset statuses for a project with automatic token refresh
 */
export const getProjectAssetStatuses = async (
  token: string, 
  projectId: string,
  keycloak?: any
): Promise<StatusesResponse> => {
  try {
    const response = await apiRequest(`${API_URL}/projects/${projectId}/assets/statuses`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    }, keycloak);

    const data: StatusesResponse = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching project asset statuses:', error);
    throw new Error(`Failed to fetch project asset statuses: ${error.message}`);
  }
};

/**
 * Get a specific asset status by ID
 */
export const getProjectAssetStatus = async (token: string, projectId: string, statusId: string): Promise<AssetStatus> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/statuses/${statusId}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: AssetStatus = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error fetching project asset status:', error);
    throw new Error(`Failed to fetch project asset status: ${error.message}`);
  }
};

/**
 * Create a new asset status
 */
export const createProjectAssetStatus = async (token: string, projectId: string, statusData: CreateStatusRequest): Promise<AssetStatus> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/statuses`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(statusData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: AssetStatus = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error creating project asset status:', error);
    throw new Error(`Failed to create project asset status: ${error.message}`);
  }
};

/**
 * Update an existing asset status
 */
export const updateProjectAssetStatus = async (token: string, projectId: string, statusId: string, statusData: UpdateStatusRequest): Promise<AssetStatus> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/statuses/${statusId}`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(statusData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: AssetStatus = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error updating project asset status:', error);
    throw new Error(`Failed to update project asset status: ${error.message}`);
  }
};

/**
 * Delete an asset status
 */
export const deleteProjectAssetStatus = async (token: string, projectId: string, statusId: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/statuses/${statusId}`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (error: any) {
    console.error('Error deleting project asset status:', error);
    throw new Error(`Failed to delete project asset status: ${error.message}`);
  }
};

/**
 * Get project asset lifecycle - full history of asset changes
 */
export const getProjectAssetLifecycle = async (token: string, projectId: string, assetId: string): Promise<AssetLifecycle> => {
  try {
    console.log('🔄 Fetching asset lifecycle:', { projectId, assetId });
    
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/${assetId}/lifecycle`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: AssetLifecycle = await response.json();
    console.log('✅ Asset lifecycle loaded:', data.length, 'entries');
    return data;
  } catch (error: any) {
    console.error('❌ Error fetching project asset lifecycle:', error);
    throw new Error(`Failed to get project asset lifecycle: ${error.message}`);
  }
};

/**
 * Get project asset warranty information
 */
export const getProjectAssetWarranty = async (token: string, projectId: string, assetId: string): Promise<AssetWarranty> => {
  try {
    console.log('🛡️ Fetching asset warranty:', { projectId, assetId });
    
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/${assetId}/warranty`, {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: AssetWarranty = await response.json();
    console.log('✅ Asset warranty loaded:', {
      id: data.id,
      provider: data.warranty_provider,
      status: data.warranty_status,
      daysRemaining: data.days_remaining
    });
    return data;
  } catch (error: any) {
    console.error('❌ Error fetching project asset warranty:', error);
    throw new Error(`Failed to get project asset warranty: ${error.message}`);
  }
};

/**
 * Update an asset warranty
 */
export const updateProjectAssetWarranty = async (token: string, projectId: string, assetId: string, warrantyData: UpdateWarrantyRequest): Promise<AssetWarranty> => {
  try {
    const response = await fetch(`${API_URL}/projects/${projectId}/assets/${assetId}/warranty`, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      },
      body: JSON.stringify(warrantyData)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: AssetWarranty = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error updating project asset warranty:', error);
    throw new Error(`Failed to update project asset warranty: ${error.message}`);
  }
}; 